import * as React from 'react';

const PWA_ROOT_SELECTOR = '.app-root--pwa-mobile';

/**
 * Keeps `--pwa-header-height` in sync with the fixed PWA header's rendered height
 * (distance from viewport top to header bottom). Used directly as `.top-pad` padding-top.
 */
export function useSyncPwaHeaderHeight(
  headerRef: React.RefObject<HTMLElement | null>,
  enabled: boolean,
  syncKey: string
): void {
  React.useLayoutEffect(() => {
    const root = document.querySelector(PWA_ROOT_SELECTOR) as HTMLElement | null;

    if (!enabled) {
      root?.style.removeProperty('--pwa-header-height');
      return undefined;
    }

    const header = headerRef.current;
    if (!header || !root) {
      return undefined;
    }

    const sync = () => {
      // Full distance from viewport top to header bottom — used directly as main padding-top.
      const bottom = Math.ceil(header.getBoundingClientRect().bottom);
      const next = `${Math.max(48, bottom)}px`;
      const current = root.style.getPropertyValue('--pwa-header-height');

      if (current !== next) {
        root.style.setProperty('--pwa-header-height', next);
      }
    };

    sync();
    const rafId = window.requestAnimationFrame(sync);
    const timeoutId = window.setTimeout(sync, 0);

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(sync);
    resizeObserver?.observe(header);

    window.addEventListener('resize', sync);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', sync);
      root.style.removeProperty('--pwa-header-height');
    };
  }, [enabled, headerRef, syncKey]);
}
